import * as React from 'react';
import { call } from 'redux-saga/effects';
import { appStore } from '../../app/AppStore';
import { getDataRoleInfos, getDatasourceInfos, getProjectInfos, getPublishedConnectionInfos, getViewInfos, getWorkbookInfos } from '../../app/sagas/spaces/SpacesSagaHelpers';
import { NavLocationSelectors } from '../../app/selectors/NavigationStateSelectors';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../app/ServiceLocator';
import { CollectionCard } from '../../app/spaces/CardView/CollectionCard';
import { ContentCardProps } from '../../app/spaces/CardView/ContentCard';
import { DataconnectionCard } from '../../app/spaces/CardView/DataconnectionCard';
import { DataRoleCard } from '../../app/spaces/CardView/DataRoleCard';
import { DatasourceCard } from '../../app/spaces/CardView/DatasourceCard';
import { FlowCard } from '../../app/spaces/CardView/FlowCard';
import { GenericCard } from '../../app/spaces/CardView/GenericCard';
import { ProjectCard } from '../../app/spaces/CardView/ProjectCard';
import { PublishedConnectionCard } from '../../app/spaces/CardView/PublishedConnectionCard';
import { RecommendationCard } from '../../app/spaces/CardView/RecommendationCard';
import { ViewCard } from '../../app/spaces/CardView/ViewCard';
import { WorkbookCard } from '../../app/spaces/CardView/WorkbookCard';
import { ConnectedCollectionDetailsDialogBody } from '../../app/spaces/Collections/CollectionDetailsDialog';
import { CollectionsResourceQuery } from '../../app/spaces/Collections/Collections';
import { ICollectionInfo } from '../../app/spaces/Collections/ICollectionInfo';
import { getAngularDatasourceDetailDialogOptions, getAngularProjectDetailDialogOptions, getAngularWorkbookDetailDialogOptions } from '../../app/spaces/DetailsDialog/AngularContentDetailsDialogOptions';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { FilterMap } from '../../app/state/ContentExplorerState';
import { TopLevelProjectId } from '../../app/state/SpaceInfo';
import { DatabasesGraphQLResourceQuery } from '../../catalog/fetchers/DatabasesGraphQLResourceQuery';
import { TablesGraphQLResourceQuery } from '../../catalog/fetchers/TablesGraphQLResourceQuery';
import { Messages } from '../../codegen/Localize';
import { imgPath } from '../../common/utilities/ImgUrl';
import { MetricDefinition } from '../../metrics/models/MetricDefinition';
import { AngularContext } from '../factories/AngularContextFactory';
import { Filter } from '../helpers/Filter';
import { LocallySortingScheduleResourceQuery } from '../helpers/LocallySortingScheduleResourceQuery';
import { ServerFilterClauses } from '../helpers/ServerFilterClauses';
import { AccelerationTasks } from '../resources/AccelerationTasks';
import { BackgroundJobs } from '../resources/BackgroundJobs';
import { DataAlerts } from '../resources/DataAlerts';
import { DataConnections } from '../resources/DataConnections';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../resources/Datasources';
import { ExtractTasks } from '../resources/ExtractTasks';
import { FlowOutputStepRuns } from '../resources/FlowOutputStepRuns';
import { Flows } from '../resources/Flows';
import { Groups } from '../resources/Groups';
import { Projects } from '../resources/Projects';
import { PublishedConnections } from '../resources/PublishedConnections';
import { RemoteRefreshSchedules } from '../resources/RemoteRefreshSchedules';
import { IResourceQuery } from '../resources/ResourceQuery';
import { RunFlowTasks } from '../resources/RunFlowTasks';
import { Schedules } from '../resources/Schedules';
import { ServerUsers } from '../resources/ServerUsers';
import { Sites } from '../resources/Sites';
import { SiteUsers } from '../resources/SiteUsers';
import { Subscriptions } from '../resources/Subscriptions';
import { Users } from '../resources/Users';
import { Views } from '../resources/Views';
import { WorkbookAccelerations } from '../resources/WorkbookAccelerations';
import { Workbooks } from '../resources/Workbooks';
import { CustomViews } from '../ServerApi/CustomViews/CustomViews';
import { nonSystemJobFilter } from '../ServerApi/types/IBackgroundJob';
import { ExploreContentApi_ContentType } from '../ServerApi/types/IContent';
import { FieldValueResourceType } from '../ServerApi/types/IFieldValuesParams';
import { FilterFieldNames, IStringArrayValuedFieldFilterClause } from '../ServerApi/types/IFilterClause';
import { DisplayMode } from '../ServerApi/types/ISessionInfo';
import { IModalOptions } from '../services/ModalService';
import { ServerService } from '../services/ServerService';
import { ContentFilterKeyName, ContentFiltersKeys } from './ContentFilters';
import { ExplorableItemType, SpaceInfoItemType } from './ContentType';
import { AccelerationTaskContentType, BackgroundJobContentType, CustomViewsContentType, DataAlertContentType, DatabaseContentType, DataConnectionContentType, DataConnectionInWorkbookContentType, DataRoleContentType, DatasourceContentType, ExtractTaskContentType, FlowContentType, FlowOutputStepRunContentType, GroupContentType, ProjectContentType, PublishedConnectionContentType, RemoteRefreshScheduleContentType, RunFlowTaskContentType, ServerUserContentType, SitesContentType, SiteUserContentType, SubscriptionContentType, TableContentType, ViewContentType, WorkbookAccelerationContentType, WorkbookContentType } from './ContentTypes';
import { getGenericDefaultFilters } from './getGenericDefaultFilters';
import { IContentInfo } from './IContentInfo';
import { IDataSourceInfo } from './IDataSourceInfo';
import { ISpaceItemDetailInfo } from './ISpaceItemDetailInfo';
import { IExplorableItemTypeInfo } from './resourceInfoParts';


export type ContentTypeFilterClause = IStringArrayValuedFieldFilterClause<ExploreContentApi_ContentType>;


interface CommonItemDefinition {
  getDetailsDialogOptions?: (angularContext: AngularContext) => IModalOptions;
  getContentInfo: (contentList: IExplorableItemTypeInfo[]) => any;
}

// Items that can be browsed in an explorer
export interface ExplorableItemDefinition extends CommonItemDefinition {
  itemType: ExplorableItemType;
  pluralName: () => string;
  emptyStateMessage: () => string; // Used as default in Explorer if only displaying this type
  contentFilterKeys: ContentFilterKeyName[];
  defaultFilters: (projectId: string) => FilterMap;
  cardComponent: React.ComponentType<ContentCardProps>;
  listViewIconUrl: string | null;
  filterClause: ContentTypeFilterClause;
  getBasicQuery: () => IResourceQuery<any>;
  fieldValueResourceType?: FieldValueResourceType;
  supportedDisplayModes: DisplayMode[];
}

// Items that are specifically content items (can be displayed in mixed content views)
export interface MixedContentItemDefinition extends ExplorableItemDefinition {
  itemType: ExploreContentApi_ContentType;
  siteFilterByTypeDropdownName: () => string;
}

// Items that that have dedicated space pages
export interface SpaceDefinition<C extends SpaceInfoItemType> extends CommonItemDefinition {
  contentType: C;
  contentSpaceUrl: (urls: SpacesSiteUrls, contentId: string, contentLuid: string) => string;
  headerIconPath: (contentInfo: ISpaceItemDetailInfo<C>) => string;
  singularName: () => string;
  detailDialogTitle?: () => string;
  detaillDialogBody?: (content: IContentInfo<C>) => JSX.Element;
}

export const ProjectDefinition: MixedContentItemDefinition & SpaceDefinition<'project'> = {
  itemType: 'project',
  contentType: 'project',
  singularName: Messages.project,
  pluralName: Messages.projects,
  emptyStateMessage: Messages.projectsEmptyMessage,
  siteFilterByTypeDropdownName: Messages.siteFilterByTypeDropdown_Projects,
  contentFilterKeys: ProjectContentType.filterKeys,
  defaultFilters: getProjectDefaultFilters,
  cardComponent: ProjectCard,
  listViewIconUrl: imgPath('spaces_medium_projects.svg'),
  filterClause: ServerFilterClauses.ContentType(['project']) as ContentTypeFilterClause,
  contentSpaceUrl: (urls: SpacesSiteUrls, contentId: string) => urls.projectUrl(contentId),
  getBasicQuery: () => {
    const projectsResource = ServiceLocator.get<Projects>('Projects');
    return projectsResource.all();
  },
  fieldValueResourceType: 'project',
  getDetailsDialogOptions: getAngularProjectDetailDialogOptions,
  getContentInfo: (contentList: IContentInfo[]) => call(getProjectInfos, contentList),
  headerIconPath: () => imgPath('spaces_masthead_project.svg'),
  supportedDisplayModes: [ 'list', 'thumbnail' ],
};

export const WorkbookDefinition: MixedContentItemDefinition & SpaceDefinition<'workbook'> = {
  itemType: 'workbook',
  contentType: 'workbook',
  singularName: Messages.workbook,
  pluralName: Messages.workbooks,
  emptyStateMessage: Messages.workbooksEmptyMessage,
  siteFilterByTypeDropdownName: Messages.siteFilterByTypeDropdown_Workbooks,
  contentFilterKeys: WorkbookContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: WorkbookCard,
  listViewIconUrl: imgPath('spaces_medium_workbooks.svg'),
  filterClause: ServerFilterClauses.ContentType(['workbook']) as ContentTypeFilterClause,
  contentSpaceUrl: (urls: SpacesSiteUrls, contentId: string) => urls.workbookUrl(contentId),
  getBasicQuery: () => {
    const workbooksResource = ServiceLocator.get<Workbooks>('Workbooks');
    return workbooksResource.all().withExtraParams(WorkbookContentType.extraQueryParams);
  },
  fieldValueResourceType: 'workbook',
  getDetailsDialogOptions: getAngularWorkbookDetailDialogOptions,
  getContentInfo: (contentList: IContentInfo[]) => call(getWorkbookInfos, contentList),
  headerIconPath: () => imgPath('spaces_masthead_workbook.svg'),
  supportedDisplayModes: [ 'list', 'thumbnail' ],
};

export const DatasourceDefinition: MixedContentItemDefinition & SpaceDefinition<'datasource'> = {
  itemType: 'datasource',
  contentType: 'datasource',
  singularName: Messages.datasource,
  pluralName: Messages.datasources,
  emptyStateMessage: Messages.datasourcesEmptyMessage,
  siteFilterByTypeDropdownName: Messages.siteFilterByTypeDropdown_Datasources,
  contentFilterKeys: DatasourceContentType.filterKeys,
  defaultFilters: getDataSourceDefaultFilters,
  cardComponent: DatasourceCard,
  listViewIconUrl: imgPath('spaces_medium_ds.svg'),
  filterClause: ServerFilterClauses.ContentType(['datasource']) as ContentTypeFilterClause,
  contentSpaceUrl: (urls: SpacesSiteUrls, contentId: string) => urls.exploreDatasourceUrl(contentId),
  getBasicQuery: () => {
    const datasourcesResource = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
    return datasourcesResource.all().withExtraParams(DatasourceContentType.extraQueryParams);
  },
  fieldValueResourceType: 'datasource',
  getDetailsDialogOptions: getAngularDatasourceDetailDialogOptions,
  getContentInfo: (contentList: IContentInfo[]) => call(getDatasourceInfos, contentList),
  headerIconPath: (contentInfo: IDataSourceInfo) => DataSources.getIconForDatasourceType(contentInfo, 'masthead'),
  supportedDisplayModes: [ 'list', 'thumbnail' ],
};

export const DataRoleDefinition: MixedContentItemDefinition & SpaceDefinition<'dataRole'> = {
  itemType: 'dataRole',
  contentType: 'dataRole',
  singularName: Messages.dataRole,
  pluralName: Messages.dataRoles,
  emptyStateMessage: Messages.dataRolesEmptyMessage,
  siteFilterByTypeDropdownName: Messages.siteFilterByTypeDropdown_Dataroles,
  contentFilterKeys: DataRoleContentType.filterKeys,
  defaultFilters: getDataRoleDefaultFilters,
  cardComponent: DataRoleCard,
  listViewIconUrl: imgPath('spaces_medium_data_role.svg'),
  filterClause: ServerFilterClauses.ContentType(['dataRole']) as ContentTypeFilterClause,
  contentSpaceUrl: (urls: SpacesSiteUrls, contentId: string) => urls.dataRoleUrl(contentId),
  getBasicQuery: () => {
    const dataRoleResource = ServiceLocator.get<DataRoles>('DataRoles');
    return dataRoleResource.all().withExtraParams(DataRoleContentType.extraQueryParams);
  },
  fieldValueResourceType: 'dataRole',
  getContentInfo: (contentList: IContentInfo[]) => call(getDataRoleInfos, contentList),
  supportedDisplayModes: [ 'list', 'thumbnail' ],
  headerIconPath: () => imgPath('spaces_masthead_datarole.svg'),
};

export const PublishedConnectionDefinition: MixedContentItemDefinition & SpaceDefinition<'publishedConnection'> = {
  itemType: 'publishedConnection',
  contentType: 'publishedConnection',
  singularName: Messages.publishedConnection,
  pluralName: Messages.publishedConnections,
  emptyStateMessage: Messages.publishedConnectionsEmptyMessage,
  siteFilterByTypeDropdownName: Messages.siteFilterByTypeDropdown_PublishedConnections,
  contentFilterKeys: PublishedConnectionContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: PublishedConnectionCard,
  listViewIconUrl: imgPath('spaces_medium_published_connection.svg'),
  filterClause: ServerFilterClauses.ContentType(['publishedConnection']) as ContentTypeFilterClause,
  contentSpaceUrl: (urls: SpacesSiteUrls, contentId: string) => urls.publishedConnectionUrl(contentId),
  getBasicQuery: () => {
    const publishedConnectionResource = ServiceLocator.get<PublishedConnections>('PublishedConnections');
    return publishedConnectionResource.all().withExtraParams(PublishedConnectionContentType.extraQueryParams);
  },
  fieldValueResourceType: 'publishedConnection',
  getContentInfo: (contentList: IContentInfo[]) => call(getPublishedConnectionInfos, contentList),
  supportedDisplayModes: [ 'list', 'thumbnail' ],
  headerIconPath: () => imgPath('spaces_masthead_published_connection.svg'),
};

export const ViewDefinition: MixedContentItemDefinition & SpaceDefinition<'view'> = {
  itemType: 'view',
  contentType: 'view',
  singularName: Messages.view,
  pluralName: Messages.views,
  emptyStateMessage: Messages.viewsEmptyMessage,
  siteFilterByTypeDropdownName: Messages.siteFilterByTypeDropdown_Views,
  contentFilterKeys: ViewContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: ViewCard,
  listViewIconUrl: imgPath('spaces_medium_views.svg'),
  filterClause: ServerFilterClauses.ContentType(['view']) as ContentTypeFilterClause,
  contentSpaceUrl: (urls: SpacesSiteUrls, contentId: string) => urls.redirectToViewUrl(contentId),
  getBasicQuery: () => {
    const viewsResource = ServiceLocator.get<Views>('Views');
    return viewsResource.all().withExtraParams(ViewContentType.extraQueryParams);
  },
  fieldValueResourceType: 'view',
  getContentInfo: (contentList: IContentInfo[]) => call(getViewInfos, contentList),
  headerIconPath: undefined,
  supportedDisplayModes: [ 'list', 'thumbnail' ],
};

export const DataConnectionDefinition: MixedContentItemDefinition = {
  itemType: 'dataconnection',
  pluralName: Messages.connections,
  emptyStateMessage: Messages.connectionsEmptyMessage,
  siteFilterByTypeDropdownName: () => '', // connections can be viewed as mixed content, but not as top-level content
  contentFilterKeys: DataConnectionContentType.filterKeys,
  defaultFilters: getDataConnectionDefaultFilters,
  cardComponent: DataconnectionCard,
  listViewIconUrl: '', // we don't display an icon for data connections
  filterClause: ServerFilterClauses.ContentType(['dataconnection']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const dataConnectionsResource = ServiceLocator.get<DataConnections>('DataConnections');
    return dataConnectionsResource.all();
  },
  fieldValueResourceType: 'dataConnection',
  getContentInfo: () => {throw new Error('getContentInfo is not implemented for dataconnection');},
  supportedDisplayModes: [ 'list', 'thumbnail' ],
};

export const FlowDefinition: MixedContentItemDefinition & SpaceDefinition<'flow'> = {
  itemType: 'flow',
  contentType: 'flow',
  singularName: Messages.flow,
  pluralName: Messages.flows,
  emptyStateMessage: Messages.flowsEmptyMessage,
  siteFilterByTypeDropdownName: Messages.siteFilterByTypeDropdown_Flows,
  contentFilterKeys: FlowContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: FlowCard,
  listViewIconUrl: imgPath('spaces_medium_flow.svg'),
  filterClause: ServerFilterClauses.ContentType(['flow']) as ContentTypeFilterClause,
  contentSpaceUrl: (urls: SpacesSiteUrls, contentId: string) => urls.flowUrl(contentId),
  getBasicQuery: () => {
    const flowsResource = ServiceLocator.get<Flows>('Flows');
    return flowsResource.all().withExtraParams(FlowContentType.extraQueryParams);
  },
  fieldValueResourceType: 'flow',
  getContentInfo: () => {throw new Error('getContentInfo is not implemented for flow');},
  headerIconPath: () => imgPath('spaces_masthead_flow.svg'),
  supportedDisplayModes: [ 'list', 'thumbnail' ],
};

export const RunFlowTaskDefinition: ExplorableItemDefinition = {
  itemType: 'runFlowTask',
  pluralName: Messages.flows,
  emptyStateMessage: Messages.flowsEmptyMessage,
  contentFilterKeys: RunFlowTaskContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: null,
  listViewIconUrl: '', // we don't display an icon for run flow tasks runs
  filterClause: ServerFilterClauses.ContentType(['runFlowTask']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const resource = ServiceLocator.get<RunFlowTasks>('RunFlowTasks');
    return resource.all().withExtraParams(RunFlowTaskContentType.extraQueryParams);
  },
  getContentInfo: () => {throw new Error('getContentInfo is not implemented for runFlowTask');},
  supportedDisplayModes: [ 'list' ]
};

export const CustomViewDefinition: ExplorableItemDefinition = {
  itemType: 'customView',
  pluralName: Messages.customViews,
  emptyStateMessage: Messages.workbooksCustomViewsEmptyMessage,
  contentFilterKeys: CustomViewsContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['customView']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const customViewsResource = ServiceLocator.get<CustomViews>('CustomViews');
    return customViewsResource.all().withExtraParams(CustomViewsContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

export const SubscriptionDefinition: ExplorableItemDefinition = {
  itemType: 'subscription',
  pluralName: Messages.subscriptions,
  emptyStateMessage: Messages.subscriptionsEmptyMessage,
  contentFilterKeys: SubscriptionContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['subscription']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const subscriptionsResource = ServiceLocator.get<Subscriptions>('Subscriptions');
    return subscriptionsResource.all().withExtraParams(SubscriptionContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

export const DataAlertDefinition: ExplorableItemDefinition = {
  itemType: 'dataalert',
  pluralName: Messages.dataAlerts,
  emptyStateMessage: Messages.dataAlertsEmptyMessage,
  contentFilterKeys: DataAlertContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['dataalert']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const dataalertsResource = ServiceLocator.get<DataAlerts>('DataAlerts');
    return dataalertsResource.all().withExtraParams(DataAlertContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

export const SiteDefinition: ExplorableItemDefinition = {
  itemType: 'site',
  pluralName: Messages.sites,
  emptyStateMessage: Messages.sitesEmptyMessage,
  contentFilterKeys: SitesContentType.filterKeys,
  defaultFilters: getNoDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['site']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const sitesResource = ServiceLocator.get<Sites>('Sites');
    return sitesResource.all().withExtraParams(SitesContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

export const ScheduleDefinition: ExplorableItemDefinition & SpaceDefinition<'schedule'> = {
  itemType: 'schedule',
  contentType: 'schedule',
  singularName: Messages.schedule,
  pluralName: Messages.schedules,
  emptyStateMessage: Messages.schedulesEmptyMessage,
  contentFilterKeys: [],
  defaultFilters: (_projectId: string) => {
    // if on site schedules page return a siteId filter
    const inServerArea = NavLocationSelectors.inServerArea(appStore.getState());
    if (!inServerArea) {
      const siteId: string = SessionSiteSelectors.siteId(appStore.getState());
      return { siteId: ServerFilterClauses.serverFilterClauses.siteId(siteId) };
    }
    return undefined;
  },
  cardComponent: GenericCard,
  listViewIconUrl: null,
  headerIconPath: () => imgPath('spaces_masthead_schedule.svg'),
  filterClause: ServerFilterClauses.ContentType(['schedule']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const schedulesResource = ServiceLocator.get<Schedules>('Schedules');
    return schedulesResource.all();
  },
  getContentInfo: () => {},
  contentSpaceUrl: () => '',
  supportedDisplayModes: [ 'list' ],
};

export const GroupDefinition: ExplorableItemDefinition & SpaceDefinition<'group'> = {
  itemType: 'siteuser',
  contentType: 'group',
  singularName: Messages.group,
  pluralName: Messages.groups,
  emptyStateMessage: Messages.groupsEmptyMessage,
  contentFilterKeys: GroupContentType.filterKeys,
  defaultFilters: getNoDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  headerIconPath: () => imgPath('spaces_masthead_groups.svg'),
  filterClause: ServerFilterClauses.ContentType(['siteuser']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const groupsResource = ServiceLocator.get<Groups>('Groups');
    return groupsResource.all();
  },
  getContentInfo: () => {},
  contentSpaceUrl: () => '',
  supportedDisplayModes: [ 'list' ],
};

export const ExtractTaskDefinition: ExplorableItemDefinition = {
  itemType: 'extractTask',
  pluralName: Messages.extractTasks,
  emptyStateMessage: Messages.extractTasksEmptyMessage,
  contentFilterKeys: [],
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['extractTask']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const extractTasksResource = ServiceLocator.get<ExtractTasks>('ExtractTasks');
    return extractTasksResource.all().withExtraParams(ExtractTaskContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ]
};

export const WorkbookAccelerationDefinition: ExplorableItemDefinition = {
  itemType: 'workbookAcceleration',
  pluralName: Messages.workbookAccelerations,
  emptyStateMessage: Messages.workbookAccelerationsEmptyMessage,
  contentFilterKeys: WorkbookAccelerationContentType.filterKeys,
  defaultFilters: getNoDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: null,
  getBasicQuery: () => {
    const workbookAccelerationResource = ServiceLocator.get<WorkbookAccelerations>('WorkbookAccelerations');
    return workbookAccelerationResource.all().withExtraParams(WorkbookAccelerationContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ]
};

export const AccelerationTaskDefinition: ExplorableItemDefinition = {
  itemType: 'accelerationTask',
  pluralName: Messages.accelerationTasks,
  emptyStateMessage: Messages.accelerationTasksEmptyMessage,
  contentFilterKeys: [],
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['accelerationTask']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const accelerationTasksResource = ServiceLocator.get<AccelerationTasks>('AccelerationTasks');
    return accelerationTasksResource.all().withExtraParams(AccelerationTaskContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ]
};

export const BackgroundJobDefinition: ExplorableItemDefinition = {
  itemType: 'backgroundJob',
  pluralName: Messages.backgroundJobs,
  emptyStateMessage: Messages.backgroundJobsEmptyMessage,
  contentFilterKeys: [
    ContentFiltersKeys.backgroundJobStatus,
    ContentFiltersKeys.backgroundJobTaskType,
    ContentFiltersKeys.backgroundJobTimeRange,
    ContentFiltersKeys.showSystemJobs,
  ],
  defaultFilters: getBackgroundJobsDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['backgroundJob']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const backgroundJobsResource = ServiceLocator.get<BackgroundJobs>('BackgroundJobs');
    return backgroundJobsResource.all().withExtraParams(BackgroundJobContentType.extraQueryParams);
  },
  getContentInfo: () => { },
  supportedDisplayModes: ['list'],
};

export const FlowOutputStepRunDefinition: ExplorableItemDefinition = {
  itemType: 'flowOutputStepRun',
  pluralName: Messages.flowRuns,
  emptyStateMessage: Messages.flowRunsEmptyMessage,
  contentFilterKeys: FlowOutputStepRunContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: null,
  listViewIconUrl: '', // we don't display an icon for flow output step runs
  filterClause: ServerFilterClauses.ContentType(['flowOutputStepRun']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const resource = ServiceLocator.get<FlowOutputStepRuns>('FlowOutputStepRuns');
    return resource.all().withExtraParams(FlowOutputStepRunContentType.extraQueryParams);
  },
  getContentInfo: () => {throw new Error('getContentInfo is not implemented for flowOutputStepRun');},
  supportedDisplayModes: [ 'list' ]
};

export const DatabaseDefinition: ExplorableItemDefinition = {
  itemType: 'database',
  pluralName: () => Messages.databasesAndFiles(),
  emptyStateMessage: () => Messages.databasesAndFilesEmptyMessage(),
  contentFilterKeys: DatabaseContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: null,
  getBasicQuery: () => new DatabasesGraphQLResourceQuery(),
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

export const TableDefinition: ExplorableItemDefinition = {
  itemType: 'table',
  pluralName: () => Messages.tables(),
  emptyStateMessage: () => Messages.tablesEmptyMessage(),
  contentFilterKeys: TableContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: null,
  getBasicQuery: () => new TablesGraphQLResourceQuery(),
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

// TFS 829184- these all need validating!
export const DataConnectionInDatasourceDefinition: ExplorableItemDefinition = {
  itemType: 'connection',
  pluralName: Messages.connections,
  emptyStateMessage: Messages.connectionsEmptyMessage,
  contentFilterKeys: [],
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['dataconnection']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const dataConnectionsResource = ServiceLocator.get<DataConnections>('DataConnections');
    return dataConnectionsResource.all().withExtraParams(DataConnectionInWorkbookContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

// TFS 829185
export const RemoteRefreshSchedulesDefinition: ExplorableItemDefinition = {
  itemType: 'remoteRefreshSchedule',
  pluralName: Messages.extractTasks,
  emptyStateMessage: Messages.extractTasksEmptyMessage,
  contentFilterKeys: [],
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['remoteRefreshSchedule']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const resource = ServiceLocator.get<RemoteRefreshSchedules>('RemoteRefreshSchedules');
    const resourceQuery = resource.all().withExtraParams(RemoteRefreshScheduleContentType.extraQueryParams);
    return new LocallySortingScheduleResourceQuery(resourceQuery);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

export const ConnectedWorkbooksInDatasourceDefinition: ExplorableItemDefinition = {
  itemType: 'connectedWorkbook',
  pluralName: Messages.connectedWorkbooks,
  emptyStateMessage: Messages.connectedWorkbooksEmptyMessage,
  contentFilterKeys: DataConnectionContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['connectedWorkbook']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const workbooksResource = ServiceLocator.get<Workbooks>('Workbooks');
    return workbooksResource.all().withExtraParams(WorkbookContentType.extraQueryParams);
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list', 'thumbnail' ],
};

export const SiteUserDefinition: SpaceDefinition<'siteuser'> & ExplorableItemDefinition = {
  itemType: 'siteuser',
  contentType: 'siteuser',
  singularName: Messages.siteUser,
  pluralName: Messages.users,
  emptyStateMessage: Messages.usersEmptyMessage,
  contentFilterKeys: SiteUserContentType.filterKeys,
  filterClause: ServerFilterClauses.ContentType(['siteuser']) as ContentTypeFilterClause,
  defaultFilters: getNoDefaultFilters,
  supportedDisplayModes: [ 'list' ],
  cardComponent: GenericCard,
  listViewIconUrl: null,
  contentSpaceUrl: (urls, id) => '', // note this is used for breadcrumbs/clicking on card and list view, //might have to be refactored to take in the entire item
  headerIconPath: (contentInfo: ISpaceItemDetailInfo<'siteuser'>) => {
    if (contentInfo.userImageUrl) {
      return contentInfo.userImageUrl;
    }
    return imgPath('user_content_type.svg');
  },
  getContentInfo: () => {},
  getBasicQuery: () => {
    const usersResource = ServiceLocator.get<SiteUsers>('SiteUsers');
    return usersResource.all();
  },
};

export const ServerUserDefinition: SpaceDefinition<'serveruser'> & ExplorableItemDefinition = {
  itemType: 'serveruser',
  contentType: 'serveruser',
  singularName: Messages.serverUser,
  pluralName: Messages.serverUsers,
  emptyStateMessage: Messages.usersEmptyMessage,
  contentFilterKeys: ServerUserContentType.filterKeys,
  filterClause: ServerFilterClauses.ContentType(['serveruser']) as ContentTypeFilterClause,
  defaultFilters: getNoDefaultFilters,
  supportedDisplayModes: [ 'list' ],
  cardComponent: GenericCard,
  listViewIconUrl: null,
  contentSpaceUrl: (urls, id) => '', // note this is used for breadcrumbs/clicking on card and list view, //might have to be refactored to take in the entire item
  headerIconPath: (contentInfo: ISpaceItemDetailInfo<'serveruser'>) => {
    if (contentInfo.userImageUrl) {
      return contentInfo.userImageUrl;
    }
    return imgPath('user_content_type.svg');
  },
  getContentInfo: () => {},
  getBasicQuery: () => {
    const usersResource = ServiceLocator.get<ServerUsers>('ServerUsers');
    return usersResource.all();
  },
};

export const SearchUserDefinition: ExplorableItemDefinition = {
  itemType: 'searchuser',
  pluralName: Messages.users,
  emptyStateMessage: Messages.usersEmptyMessage,
  contentFilterKeys: [ContentFiltersKeys.search],
  defaultFilters: getNoDefaultFilters,
  cardComponent: GenericCard,
  listViewIconUrl: null,
  filterClause: ServerFilterClauses.ContentType(['searchuser']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    const users = ServiceLocator.get<Users>('Users');
    return users.all().withExtraParams({
      'getItemCounts': true
    });
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list' ],
};

export const RecommendedViewDefinition: ExplorableItemDefinition = {
  itemType: 'recommendedView',
  pluralName: Messages.recommendations,
  emptyStateMessage: Messages.recommendationsEmptyMessage,
  contentFilterKeys: null,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: RecommendationCard,
  listViewIconUrl: null,
  filterClause: null,
  getBasicQuery: () => null,
  getContentInfo: () => {},
  supportedDisplayModes: [ 'thumbnail' ],
};

export const CollectionDefinition: SpaceDefinition<'collection'> & ExplorableItemDefinition = {
  itemType: 'collection',
  singularName: Messages.collection,
  pluralName: Messages.collections,
  emptyStateMessage: Messages.collectionsEmptyMessage,
  contentFilterKeys: [
    // ContentFiltersKeys.search, TFSID: 1114216
    ContentFiltersKeys.createdAfter,
    ContentFiltersKeys.createdBefore,
    // ContentFiltersKeys.collectionVisibility  TFSID:1067708
  ],
  defaultFilters: getNoDefaultFilters,
  cardComponent: CollectionCard,
  listViewIconUrl: imgPath('collection-24px-xxxlarge.svg'),
  filterClause: ServerFilterClauses.ContentType(['collection']) as ContentTypeFilterClause,
  getBasicQuery: () => {
    return new CollectionsResourceQuery();
  },
  getContentInfo: () => {},
  supportedDisplayModes: [ 'list', 'thumbnail' ],
  contentType: 'collection',
  contentSpaceUrl: (urls, _contentId, luid) => urls.collectionUrl(luid),
  headerIconPath: () => undefined, // we don't want to show a header icon for collections
  detailDialogTitle: Messages.collectionsDetailsTitle,
  detaillDialogBody: (content: IContentInfo<'collection'>) => {
    return <ConnectedCollectionDetailsDialogBody collection={content as ICollectionInfo}/>;
  }
};

export const MixedContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  MetricDefinition,
  DatasourceDefinition,
  FlowDefinition,
  DataRoleDefinition,
  PublishedConnectionDefinition,
];

export const ExplorableItemDefinitions: Record<ExplorableItemType, ExplorableItemDefinition> = {
  project: ProjectDefinition,
  workbook: WorkbookDefinition,
  datasource: DatasourceDefinition,
  dataRole: DataRoleDefinition,
  view: ViewDefinition,
  dataconnection: DataConnectionDefinition,
  flow: FlowDefinition,
  customView: CustomViewDefinition,
  subscription: SubscriptionDefinition,
  accelerationTask: AccelerationTaskDefinition,
  extractTask: ExtractTaskDefinition,
  workbookAcceleration: WorkbookAccelerationDefinition,
  connection: DataConnectionInDatasourceDefinition,
  remoteRefreshSchedule: RemoteRefreshSchedulesDefinition,
  connectedWorkbook: ConnectedWorkbooksInDatasourceDefinition,
  flowOutputStepRun: FlowOutputStepRunDefinition,
  backgroundJob: BackgroundJobDefinition,
  database: DatabaseDefinition,
  table: TableDefinition,
  metric: MetricDefinition,
  siteuser: SiteUserDefinition,
  serveruser: ServerUserDefinition,
  searchuser: SearchUserDefinition,
  recommendedView: RecommendedViewDefinition,
  dataalert: DataAlertDefinition,
  site: SiteDefinition,
  schedule: ScheduleDefinition,
  runFlowTask: RunFlowTaskDefinition,
  group: GroupDefinition,
  collection: CollectionDefinition,
  publishedConnection: PublishedConnectionDefinition,
};

export const ContentSpaceDefinitions: { [C in SpaceInfoItemType]: SpaceDefinition<C> } = {
  project: ProjectDefinition,
  workbook: WorkbookDefinition,
  datasource: DatasourceDefinition,
  view: ViewDefinition,
  flow: FlowDefinition,
  dataRole: DataRoleDefinition,
  group: GroupDefinition,
  siteuser: SiteUserDefinition,
  serveruser: ServerUserDefinition,
  schedule: ScheduleDefinition,
  metric: MetricDefinition,
  collection: CollectionDefinition,
  publishedConnection: PublishedConnectionDefinition,
};

function getNoDefaultFilters(_projectId: string) {
  return {};
}

function getDataSourceDefaultFilters(projectId: string) {
  let filterMap: FilterMap = getGenericDefaultFilters(projectId);
  filterMap[ContentFiltersKeys.showEmbedded] = ServerFilterClauses.serverFilterClauses[ContentFiltersKeys.showEmbedded]('false');
  return filterMap;
}

function getDataRoleDefaultFilters(projectId: string) {
  const filterMap: FilterMap = getGenericDefaultFilters(projectId);
  return filterMap;
}


function getDataConnectionDefaultFilters(projectId: string): FilterMap {
  let filterMap: FilterMap = getDataSourceDefaultFilters(projectId);

  // Since, in the current UX, data connections are listed under the data sources tab,
  // data connections should always be filtered to those contained by a data source
  // (not those contained by a flow), whether flows are enabled or not.
  filterMap[ContentFiltersKeys.containerType] = ServerFilterClauses.create({ containerType: 'datasource' })[0];

  return filterMap;
}

function getProjectDefaultFilters(projectId: string) {
  if (projectId === TopLevelProjectId) {
    const filterClause = ServerFilterClauses.serverFilterClauses[ContentFiltersKeys.allProjects];
    return { allProjects: filterClause('false') };
  } else {
    return { parentProject: ServerFilterClauses.create({ parentProject: projectId })[0] };
  }
}

function getBackgroundJobsDefaultFilters(projectId: string) {
  const serverService = ServiceLocator.get<ServerService>('ServerService');
  const isServerPage = NavLocationSelectors.inServerArea(appStore.getState());

  return {
    backgroundJobTaskType: nonSystemJobFilter,
    siteId: isServerPage ? undefined : Filter.stringFieldEq(FilterFieldNames.siteId, serverService.sessionSiteId())
  };
}
